// "Preparing" demo profiles by regenerating all UUIDs to avoid conflicts between different users' demo profiles when importing into the database.

import type { Profile } from "~/types"

/**
 * Prepares demo profiles by regenerating all UUIDs to avoid conflicts between different users' demo profiles when importing into the database.
 * This function modifies the profiles array in place, updating all IDs and their references throughout the profile structure.
 *
 * @param profiles - Array of profiles to prepare (modified in place)
 * @example
 * const demoProfiles = [...profiles]
 * prepareDemoProfiles(demoProfiles)
 * // All IDs in demoProfiles are now unique UUIDs
 */
export function prepareDemoProfiles(profiles: Profile[]): void {
  profiles.forEach((profile: Profile) => {
    // Create mapping of old IDs to new UUIDs
    const idMappings = {
      units: new Map<string, string>(),
      modelTypes: new Map<string, string>(),
      weapons: new Map<string, string>(),
      abilities: new Map<string, string>(),
    }

    // Generate new profile ID
    profile.id = crypto.randomUUID()

    // First pass: create ID mappings
    for (const unit of profile.units) {
      const newUnitId = crypto.randomUUID()
      idMappings.units.set(unit.id, newUnitId)

      // Unit abilities
      unit.abilities.forEach((ability) => {
        const newAbilityId = crypto.randomUUID()
        idMappings.abilities.set(ability.id, newAbilityId)
      })

      // ModelTypes
      for (const modelType of unit.model_types) {
        const newModelTypeId = crypto.randomUUID()
        idMappings.modelTypes.set(modelType.id, newModelTypeId)

        // Weapons
        for (const weapon of modelType.weapons) {
          const newWeaponId = crypto.randomUUID()
          idMappings.weapons.set(weapon.id, newWeaponId)

          // Weapon abilities
          weapon.abilities.forEach((ability) => {
            const newAbilityId = crypto.randomUUID()
            idMappings.abilities.set(ability.id, newAbilityId)
          })
        }
      }
    }

    // Second pass: apply new IDs and update references
    for (const unit of profile.units) {
      unit.id = idMappings.units.get(unit.id) || unit.id

      // Update unit ability references
      unit.abilities_selected = unit.abilities_selected.map(
        (id: string) => idMappings.abilities.get(id) || id
      )

      // Update unit abilities
      unit.abilities.forEach((ability) => {
        ability.id = idMappings.abilities.get(ability.id) || ability.id
      })

      // ModelTypes
      for (const modelType of unit.model_types) {
        modelType.id = idMappings.modelTypes.get(modelType.id) || modelType.id

        // Weapons
        for (const weapon of modelType.weapons) {
          weapon.id = idMappings.weapons.get(weapon.id) || weapon.id

          // Weapon abilities
          weapon.abilities.forEach((ability) => {
            ability.id = idMappings.abilities.get(ability.id) || ability.id
          })
        }
      }
    }

    // Update weapon references
    profile.weapons_selected_melee = profile.weapons_selected_melee.map(
      (id: string) => idMappings.weapons.get(id) || id
    )
    profile.weapons_selected_ranged = profile.weapons_selected_ranged.map(
      (id: string) => idMappings.weapons.get(id) || id
    )

    // Update order_slain_model_types references
    profile.order_slain_model_types.forEach((item) => {
      item.unitId = idMappings.units.get(item.unitId) || item.unitId
      item.modelTypeId =
        idMappings.modelTypes.get(item.modelTypeId) || item.modelTypeId
    })

    // Update computed_leaders references
    if (profile.computed_leaders) {
      profile.computed_leaders.forEach((leader) => {
        leader.unitId = idMappings.units.get(leader.unitId) || leader.unitId
      })
    }
  })
}
